import { Box, Divider, IconButton, Link, Stack, Tooltip, alpha, styled, useTheme } from "@mui/material";

import { useContext } from "react";
import { useSelector } from "react-redux";

import { PATH } from "@/configs/router/routerConfig";
import { SidebarContext, widthMenuHide } from "@/contexts/SidebarContext";
import { User } from "@/models/user";
import { rootReducerName } from "@/store/rootReducer";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import BreadcrumbsSidebar from "./Breadcrumbs";
import HeaderButtons from "./Buttons";
import HeaderUserbox from "./Userbox";

const CloseTwoToneIcon = dynamic(() => import("@mui/icons-material/CloseTwoTone"), { ssr: false });
const MenuTwoToneIcon = dynamic(() => import("@mui/icons-material/MenuTwoTone"), { ssr: false });

type Props = {
  fullSize?: boolean;
};

type HeaderWrapperProps = {
  fullSize?: boolean;
  menuState: boolean;
};

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullSize" && prop !== "menuState"
})<HeaderWrapperProps>(
  ({ theme, fullSize = false, menuState }) => `
          height: ${theme.header.height};
          color: ${theme.header.textColor};
          padding: ${theme.spacing(0, 2)};
          right: 0;
          z-index: 6;
          background-color: ${alpha(theme.header.background, 1)};
          backdrop-filter: blur(3px);
          position: fixed;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          @media (min-width: ${theme.breakpoints.values.lg}px) {
              left: ${fullSize ? 0 : !menuState ? widthMenuHide : theme.sidebar.width};
              width: ${fullSize ? "100%" : !menuState ? `calc(100% - ${widthMenuHide})` : "auto"};
          }
  `
);

const LogoWrapper = styled(Link)(
  ({ theme }) => `
        color: ${theme.palette.text.primary};
        display: flex;
        text-decoration: none;
        width: 100%;
        padding-left: 15px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        font-weight: ${theme.typography.fontWeightBold};
`
);

const LogoSignWrapper = styled(Box)(
  () => `
        width: 40px;
        height: 40px;
`
);

function Header(props: Props) {
  const { t } = useTranslation();
  const { sidebarToggle, toggleSidebar, getBreadcrumbsAndTitle, menuState } = useContext(SidebarContext);
  const { fullSize = false } = props;
  const theme = useTheme();
  const router = useRouter();
  const user: User = useSelector((state) => state[rootReducerName.auth].user);
  const breadcrumbs = getBreadcrumbsAndTitle(router);

  return (
    <HeaderWrapper
      fullSize={fullSize}
      menuState={menuState}
      display="flex"
      alignItems="center"
      sx={{
        boxShadow:
          theme.palette.mode === "dark"
            ? `${theme.header.boxShadow}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(theme.colors.alpha.black[100], 0.2)}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`
      }}
    >
      <Box
        sx={{
          display: { sm: "none", xs: "none" }
        }}
      >
        <LogoWrapper href={PATH.OVERVIEW}>
          <LogoSignWrapper>
            <img alt="logo" src="/Sellpath-logo.png" />
          </LogoSignWrapper>
          <span style={{ marginLeft: "10px" }}>{t("SELLPATH")}</span>
        </LogoWrapper>
      </Box>
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        alignItems="center"
        width={"30%"}
        spacing={2}
        sx={{
          display: { sm: "flex" }
        }}
      >
        {breadcrumbs?.breadcrumbs.length > 0 && (
          <BreadcrumbsSidebar
            breadcrumbs={breadcrumbs}
            // Adjust this logic to match your actual tenant sandbox property
            isSandboxTenant={user?.isSandboxTenant}
          />
        )}
      </Stack>

      <Box display="flex" alignItems="center">
        <Box
          alignItems="center"
          sx={{
            display: { sm: "flex", xs: "flex" }
          }}
        >
          <HeaderButtons />
          {user ? <HeaderUserbox /> : null}
        </Box>
        <Box
          component="span"
          sx={{
            ml: 2,
            display: { lg: "none", xs: "inline-block" }
          }}
        >
          <Tooltip arrow title={t("Toggle Menu")}>
            <IconButton
              color="primary"
              onClick={toggleSidebar}
              aria-label={t(!sidebarToggle ? "Open navigation menu" : "Close navigation menu")}
            >
              {!sidebarToggle ? <MenuTwoToneIcon fontSize="small" /> : <CloseTwoToneIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </HeaderWrapper>
  );
}

export default Header;
